import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const normalizePhone = (p) => {
  if (!p) return '';
  let d = String(p).replace(/\D/g, '');
  if (d.startsWith('880')) d = d.substring(3);
  if (d.startsWith('0')) d = d.substring(1);
  return d ? `+880${d}` : '';
};

const cacheKey = (phone) => `nexora_contract_cache_${phone}`;
const uidCacheKey = (uid) => `nexora_contract_cache_uid_${uid}`;

const readCache = (phone) => {
  try {
    if (!phone) return [];
    const value = localStorage.getItem(cacheKey(phone));
    const parsed = value ? JSON.parse(value) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const readUidCache = (uid) => {
  try {
    if (!uid) return [];
    const value = localStorage.getItem(uidCacheKey(uid));
    const parsed = value ? JSON.parse(value) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeCache = (phone, contacts, uid = '') => {
  try {
    const data = JSON.stringify(contacts);
    if (phone) localStorage.setItem(cacheKey(phone), data);
    if (uid) localStorage.setItem(uidCacheKey(uid), data);
  } catch {}
};

const Contract = () => {
  const navigate = useNavigate();
  const [myPhone, setMyPhone] = useState('');
  const [contacts, setContacts] = useState(() => {
    try {
      const uid = localStorage.getItem('nexora_user_uid');
      return readUidCache(uid);
    } catch {
      return [];
    }
  });
  const [visibleCount, setVisibleCount] = useState(() => {
    try {
      const uid = localStorage.getItem('nexora_user_uid');
      return Math.min(20, readUidCache(uid).length);
    } catch {
      return 20;
    }
  });
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [contactName, setContactName] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkMessage, setCheckMessage] = useState('');

  const [popup, setPopup] = useState({
    show: false, type: '', title: '', message: ''
  });

  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [actionPhone, setActionPhone] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editPhone, setEditPhone] = useState('');
  const [editName, setEditName] = useState('');
  const [summaryFlipped, setSummaryFlipped] = useState(false);

  const sentinelRef = useRef(null);
  const checkTimer = useRef(null);
  const tapTimer = useRef(null);
  const tapCount = useRef(0);

  const showPopup = (type, title, message) => {
    setPopup({ show: true, type, title, message });
  };

  const closePopup = () => {
    setPopup({ show: false, type: '', title: '', message: '' });
  };

  useEffect(() => {
    let alive = true;

    const load = async () => {
      const uid =
        localStorage.getItem('nexora_user_uid') ||
        auth.currentUser?.uid;

      if (!uid) {
        navigate('/login');
        return;
      }

      // Show cached contacts immediately. Firestore refresh continues in the background.
      const cachedImmediately = readUidCache(uid);
      if (cachedImmediately.length) {
        setContacts(cachedImmediately);
        setVisibleCount(Math.min(20, cachedImmediately.length));
      }

      try {
        const userSnap = await getDoc(doc(db, 'users', uid));

        if (!alive || !userSnap.exists()) return;

        const user = userSnap.data();
        const phone = normalizePhone(user.phoneNumber);

        if (!phone) {
          navigate('/settings');
          return;
        }

        setMyPhone(phone);

        const ownContact = {
          phoneNumber: phone,
          name: 'You',
          photoURL: user.photoURL || '',
          uid: user.uid || uid,
          isSelf: true
        };

        const cached = readCache(phone);
        if (cached.length) {
          const withoutSelf = cached.filter(item => item.phoneNumber !== phone);
          const cachedWithSelf = [ownContact, ...withoutSelf];
          setContacts(cachedWithSelf);
          setVisibleCount(Math.min(20, cachedWithSelf.length));
        }

        const connectionSnap = await getDoc(
          doc(db, 'connections', phone)
        );

        if (!alive) return;

        const raw = connectionSnap.exists()
          ? connectionSnap.data().contacts || {}
          : {};

        const list = Object.entries(raw)
          .map(([number, value]) => {
            const normalized = normalizePhone(number);
            if (!normalized) return null;

            if (typeof value === 'string') {
              return {
                phoneNumber: normalized,
                name: value,
                photoURL: ''
              };
            }

            return {
              phoneNumber: normalized,
              name: value?.name || normalized,
              photoURL: value?.photoURL || '',
              uid: value?.uid || ''
            };
          })
          .filter(Boolean);

        const oldMap = new Map(
          cached.map((item) => [item.phoneNumber, item])
        );

        const merged = list.map((item) => ({
          ...oldMap.get(item.phoneNumber),
          ...item,
          name: item.name || oldMap.get(item.phoneNumber)?.name || item.phoneNumber,
          photoURL: item.photoURL || oldMap.get(item.phoneNumber)?.photoURL || ''
        }));

        const mergedWithSelf = [ownContact, ...merged.filter(item => item.phoneNumber !== phone)];

        setContacts(mergedWithSelf);
        setVisibleCount(Math.min(20, mergedWithSelf.length));
        writeCache(phone, mergedWithSelf, uid);

        // Resolve missing profile information silently.
        const needsProfile = mergedWithSelf.filter(
          (item) => !item.photoURL || !item.name || item.name === item.phoneNumber
        );

        if (needsProfile.length) {
          const resolved = await Promise.all(
            needsProfile.map(async (item) => {
              try {
                const snap = await getDoc(doc(db, 'users', item.phoneNumber));
                if (!snap.exists()) return item;
                const data = snap.data();
                return {
                  ...item,
                  name: data.fullName || item.name || item.phoneNumber,
                  photoURL: data.photoURL || item.photoURL || '',
                  uid: data.uid || item.uid || ''
                };
              } catch {
                return item;
              }
            })
          );

          if (!alive) return;

          const map = new Map(
            resolved.map((item) => [item.phoneNumber, item])
          );

          setContacts((current) => {
            const updated = current.map(
              (item) => map.get(item.phoneNumber) || item
            );
            writeCache(phone, updated, uid);
            return updated;
          });
        }
      } catch (error) {
        console.error('Contract list load failed:', error);
      }
    };

    load();

    return () => {
      alive = false;
      clearTimeout(checkTimer.current);
      clearTimeout(tapTimer.current);
    };
  }, [navigate]);

  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setVisibleCount((count) =>
          Math.min(count + 20, filteredContacts.length)
        );
      },
      { rootMargin: '500px 0px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  });

  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;

    return contacts.filter((item) =>
      String(item.name || '').toLowerCase().includes(q) ||
      String(item.phoneNumber || '').toLowerCase().includes(q)
    );
  }, [contacts, search]);

  const visibleContacts = filteredContacts.slice(0, visibleCount);

  useEffect(() => {
    if (!addOpen) return;

    const normalized = normalizePhone(phoneInput);
    setFoundUser(null);
    setCheckMessage('');

    if (!normalized || normalized.length < 14) {
      setChecking(false);
      return;
    }

    if (contacts.some((item) => item.phoneNumber === normalized)) {
      setChecking(false);
      setCheckMessage('This number is already in your Contract List.');
      return;
    }

    setChecking(true);
    clearTimeout(checkTimer.current);

    checkTimer.current = setTimeout(async () => {
      try {
        const snap = await getDoc(doc(db, 'users', normalized));

        if (snap.exists()) {
          const data = snap.data();
          setFoundUser({
            phoneNumber: normalized,
            uid: data.uid || normalized,
            defaultName: data.fullName || ''
          });
          setContactName(data.fullName || '');
          setCheckMessage('');
        } else {
          setFoundUser(null);
          setCheckMessage('এই নাম্বারে কোনো Nexora ইউজার পাওয়া যায়নি।');
        }
      } catch (error) {
        console.error('User check failed:', error);
        setFoundUser(null);
        setCheckMessage('নাম্বারটি যাচাই করা সম্ভব হয়নি। আবার চেষ্টা করুন।');
      } finally {
        setChecking(false);
      }
    }, 450);

    return () => clearTimeout(checkTimer.current);
  }, [phoneInput, addOpen, contacts]);

  const closeAdd = () => {
    setAddOpen(false);
    setPhoneInput('');
    setContactName('');
    setFoundUser(null);
    setCheckMessage('');
    setChecking(false);
  };

  const saveNewContact = async () => {
    const name = contactName.trim();

    if (!foundUser) return;

    if (!name) {
      showPopup('error', 'Name Required', 'Please enter a name for this contact.');
      return;
    }

    try {
      const ref = doc(db, 'connections', myPhone);
      const snap = await getDoc(ref);
      const existing = snap.exists() ? snap.data().contacts || {} : {};

      const updatedContacts = {
        ...existing,
        [foundUser.phoneNumber]: {
          name,
          phoneNumber: foundUser.phoneNumber,
          uid: foundUser.uid
        }
      };

      if (snap.exists()) {
        await updateDoc(ref, { contacts: updatedContacts });
      } else {
        // If the connections document does not exist yet, this will
        // require setDoc. Most Nexora accounts already have this document.
        showPopup(
          'error',
          'Unable to Save',
          'Your Contract List document does not exist yet.'
        );
        return;
      }

      const newContact = {
        phoneNumber: foundUser.phoneNumber,
        name,
        photoURL: '',
        uid: foundUser.uid
      };

      const updatedList = [
        ...contacts.filter(
          (item) => item.phoneNumber !== newContact.phoneNumber
        ),
        newContact
      ];

      setContacts(updatedList);
      setVisibleCount(Math.min(20, updatedList.length));
      writeCache(myPhone, updatedList, localStorage.getItem('nexora_user_uid'));
      closeAdd();

      // Profile image is loaded silently after the contact is saved.
      getDoc(doc(db, 'users', newContact.phoneNumber))
        .then((userSnap) => {
          if (!userSnap.exists()) return;
          const data = userSnap.data();

          setContacts((current) => {
            const updated = current.map((item) =>
              item.phoneNumber === newContact.phoneNumber
                ? { ...item, photoURL: data.photoURL || '' }
                : item
            );
            writeCache(myPhone, updated, localStorage.getItem('nexora_user_uid'));
            return updated;
          });
        })
        .catch(() => {});

      showPopup('success', 'Successful', 'Contact saved successfully.');
    } catch (error) {
      console.error('Save contact failed:', error);
      showPopup(
        'error',
        'Unable to Save',
        'The contact could not be saved. Please try again.'
      );
    }
  };

  const handleContactTap = (phone) => {
    if (phone === myPhone) return;

    if (selectionMode) {
      setSelected((current) =>
        current.includes(phone)
          ? current.filter((item) => item !== phone)
          : [...current, phone]
      );
      return;
    }

    tapCount.current += 1;
    clearTimeout(tapTimer.current);

    tapTimer.current = setTimeout(() => {
      tapCount.current = 0;
    }, 600);

    if (tapCount.current >= 3) {
      tapCount.current = 0;
      setActionPhone(phone);
      setSelected([]);
    }
  };

  const startSelection = () => {
    if (!actionPhone) return;
    setSelectionMode(true);
    setSelected([actionPhone]);
    setActionPhone('');
  };

  const cancelSelection = () => {
    setSelectionMode(false);
    setSelected([]);
    setActionPhone('');
  };

  const openEdit = () => {
    const contact = contacts.find(
      (item) => item.phoneNumber === actionPhone
    );

    if (!contact) return;

    setEditPhone(contact.phoneNumber);
    setEditName(contact.name || '');
    setEditOpen(true);
    setActionPhone('');
  };

  const saveEdit = async () => {
    const name = editName.trim();

    if (!name) {
      showPopup('error', 'Name Required', 'Please enter a contact name.');
      return;
    }

    try {
      const ref = doc(db, 'connections', myPhone);
      const snap = await getDoc(ref);

      if (!snap.exists()) return;

      const data = snap.data();
      const existing = data.contacts || {};

      if (!existing[editPhone]) return;

      existing[editPhone] = {
        ...existing[editPhone],
        name
      };

      await updateDoc(ref, { contacts: existing });

      const updated = contacts.map((item) =>
        item.phoneNumber === editPhone ? { ...item, name } : item
      );

      setContacts(updated);
      writeCache(myPhone, updated, localStorage.getItem('nexora_user_uid'));
      setEditOpen(false);

      showPopup(
        'success',
        'Successful',
        'Contact details updated successfully.'
      );
    } catch (error) {
      console.error('Edit contact failed:', error);
      showPopup(
        'error',
        'Unable to Update',
        'Contact details could not be updated.'
      );
    }
  };

  const deleteSelected = async () => {
    if (!selected.length) return;

    try {
      const ref = doc(db, 'connections', myPhone);
      const snap = await getDoc(ref);

      if (!snap.exists()) return;

      const data = snap.data();
      const existing = data.contacts || {};

      selected.forEach((phone) => {
        delete existing[phone];
      });

      await updateDoc(ref, { contacts: existing });

      const selectedSet = new Set(selected);
      const updated = contacts.filter(
        (item) => !selectedSet.has(item.phoneNumber)
      );

      setContacts(updated);
      setVisibleCount(Math.min(visibleCount, updated.length));
      writeCache(myPhone, updated, localStorage.getItem('nexora_user_uid'));

      const count = selected.length;
      cancelSelection();

      showPopup(
        'success',
        'Successful',
        `${count} contact${count > 1 ? 's' : ''} deleted successfully.`
      );
    } catch (error) {
      console.error('Delete contacts failed:', error);
      showPopup(
        'error',
        'Unable to Delete',
        'Selected contacts could not be deleted.'
      );
    }
  };

  const openChat = (phone) => {
    navigate(`/chat/${encodeURIComponent(phone)}`);
  };

  const defaultAvatar = (name) =>
    `https://api.dicebear.com/10.x/initials/svg?seed=${encodeURIComponent(name || 'User')}`;

  return (
    <div style={styles.page}>
      <style>{`
        * {
          box-sizing: border-box;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        body {
          margin: 0;
          background: #fafafa;
        }

        .search-input,
        .popup-input {
          width: 100%;
          height: 50px;
          border: 1px solid #e2e8f0;
          border-radius: 15px;
          outline: none;
          background: #f8fafc;
          padding: 0 15px;
          color: #0f172a;
          font-size: 14px;
        }

        .search-input {
          border: 0;
          background: transparent;
        }

        .search-input:focus,
        .popup-input:focus {
          border-color: #94a3b8;
          background: #fff;
          box-shadow: 0 0 0 3px rgba(148,163,184,.12);
        }

        .search-input::placeholder,
        .popup-input::placeholder {
          color: #94a3b8;
        }

        .message-button,
        .action-button,
        .add-button,
        .primary-button {
          border: 0;
          cursor: pointer;
          font-weight: 800;
        }

        .message-button {
          height: 38px;
          min-width: 78px;
          padding: 0 12px;
          border-radius: 12px;
          background: #eefbf3;
          color: #269c5b;
          font-size: 12px;
        }

        .primary-button {
          width: 100%;
          height: 50px;
          border-radius: 15px;
          background: #1e293b;
          color: #fff;
          font-size: 14px;
        }

        .action-button {
          height: 38px;
          padding: 0 13px;
          border-radius: 12px;
          font-size: 12px;
        }

        .popup-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(15,23,42,.38);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
        }

        .popup-card {
          width: 100%;
          max-width: 370px;
          max-height: 88vh;
          overflow-y: auto;
          padding: 24px;
          border-radius: 25px;
          background: #fff;
          box-shadow: 0 24px 70px rgba(15,23,42,.22);
          animation: popupIn .22s ease-out;
        }

        @keyframes popupIn {
          from {
            opacity: 0;
            transform: translateY(12px) scale(.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .success-icon,
        .error-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 15px;
          font-size: 30px;
          font-weight: 900;
        }

        .success-icon {
          background: #dcfce7;
          color: #16a34a;
        }

        .error-icon {
          background: #fee2e2;
          color: #dc2626;
        }

        .summary-flip {
          perspective: 1000px;
          margin-bottom: 12px;
          cursor: pointer;
        }

        .summary-flip-inner {
          position: relative;
          min-height: 104px;
          transform-style: preserve-3d;
          transition: transform .55s cubic-bezier(.22,1,.36,1);
        }

        .summary-flip.is-flipped .summary-flip-inner {
          transform: rotateY(180deg);
        }

        .summary-face {
          position: absolute;
          inset: 0;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          background: #fff;
          border: 1px solid #f1f5f9;
          border-radius: 20px;
          padding: 16px;
        }

        .summary-back {
          transform: rotateY(180deg);
          justify-content: center;
          flex-direction: column;
          align-items: stretch;
          background: linear-gradient(135deg, #f8fffa, #ffffff);
          border-color: #d9f5e1;
        }

        .summary-back-title {
          color: #166534;
          font-size: 13px;
          font-weight: 900;
          text-align: center;
          margin-bottom: 10px;
        }

        .summary-create-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .summary-create-button {
          border: 0;
          border-radius: 12px;
          padding: 11px 8px;
          background: #1e293b;
          color: #fff;
          font-size: 11px;
          font-weight: 800;
          cursor: pointer;
        }

        .summary-create-button.channel {
          background: #62b875;
        }
      `}</style>

      <div style={styles.card}>
        <div style={styles.header}>
          <button
            type="button"
            style={styles.backButton}
            onClick={() => navigate('/settings')}
          >
            ←
          </button>

          <h2 style={styles.headerTitle}>Contract List</h2>

          <div style={{ width: 38 }} />
        </div>

        <div style={styles.content}>
          <div
            className={`summary-flip ${summaryFlipped ? 'is-flipped' : ''}`}
            onDoubleClick={() => setSummaryFlipped(current => !current)}
            title="Double-click to create a group or channel"
          >
            <div className="summary-flip-inner">
              <div className="summary-face">
                <div>
                  <div style={styles.summaryLabel}>Total Contracts</div>
                  <div style={styles.summaryNumber}>{contacts.length}</div>
                </div>

                <button
                  type="button"
                  className="add-button"
                  style={styles.addButton}
                  onClick={(event) => {
                    event.stopPropagation();
                    setAddOpen(true);
                  }}
                >
                  + Add new
                </button>
              </div>

              <div className="summary-face summary-back">
                <div className="summary-back-title">Create something new</div>
                <div className="summary-create-row">
                  <button
                    type="button"
                    className="summary-create-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate('/group');
                    }}
                  >
                    + Create New Group
                  </button>
                  <button
                    type="button"
                    className="summary-create-button channel"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate('/channel');
                    }}
                  >
                    + Create New Channel
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.searchCard}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              className="search-input"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setVisibleCount(20);
              }}
              placeholder="Search by name or phone"
            />
          </div>

          {actionPhone && !selectionMode && (
            <div style={styles.actionBar}>
              <span style={styles.actionText}>Contact selected</span>

              <div style={styles.actionButtons}>
                <button
                  type="button"
                  className="action-button"
                  style={{
                    background: '#eef2ff',
                    color: '#475569'
                  }}
                  onClick={openEdit}
                >
                  Edit Detail
                </button>

                <button
                  type="button"
                  className="action-button"
                  style={{
                    background: '#1e293b',
                    color: '#fff'
                  }}
                  onClick={startSelection}
                >
                  Select
                </button>
              </div>
            </div>
          )}

          {selectionMode && (
            <div style={styles.selectionBar}>
              <div>
                <div style={styles.selectionTitle}>
                  {selected.length} selected
                </div>
                <div style={styles.selectionSubtitle}>
                  Tap other contacts to select
                </div>
              </div>

              <div style={styles.actionButtons}>
                <button
                  type="button"
                  className="action-button"
                  style={{
                    background: '#f1f5f9',
                    color: '#475569'
                  }}
                  onClick={cancelSelection}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="action-button"
                  style={{
                    background: selected.length ? '#fee2e2' : '#f1f5f9',
                    color: selected.length ? '#dc2626' : '#94a3b8'
                  }}
                  disabled={!selected.length}
                  onClick={deleteSelected}
                >
                  Delete
                </button>
              </div>
            </div>
          )}

          <div style={styles.list}>
            {visibleContacts.map((contact) => {
              const selectedNow = selected.includes(contact.phoneNumber);
              const image =
                contact.photoURL ||
                defaultAvatar(contact.name || contact.phoneNumber);

              return (
                <div
                  key={contact.phoneNumber}
                  style={{
                    ...styles.contactCard,
                    ...(selectedNow ? styles.selectedCard : {})
                  }}
                  onClick={() => handleContactTap(contact.phoneNumber)}
                >
                  <div style={styles.contactLeft}>
                    <div style={styles.avatarWrap}>
                      <img
                        src={image}
                        alt=""
                        style={styles.avatar}
                        onError={(e) => {
                          e.currentTarget.src =
                            defaultAvatar(contact.phoneNumber);
                        }}
                      />

                      {selectedNow && (
                        <div style={styles.checkBadge}>✓</div>
                      )}
                    </div>

                    <div style={styles.contactInfo}>
                      <div style={styles.contactName}>
                        {contact.name || contact.phoneNumber}
                      </div>

                      <div style={styles.contactPhone}>
                        {contact.phoneNumber}
                      </div>
                    </div>
                  </div>

                  {!selectionMode && (
                    <button
                      type="button"
                      className="message-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openChat(contact.phoneNumber);
                      }}
                    >
                      Message
                    </button>
                  )}
                </div>
              );
            })}

            {contacts.length === 0 && (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>👥</div>
                <div style={styles.emptyTitle}>No Contacts Yet</div>
                <div style={styles.emptyText}>
                  Add a Nexora user to your Contract List.
                </div>
              </div>
            )}

            {contacts.length > 0 && filteredContacts.length === 0 && (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>🔍</div>
                <div style={styles.emptyTitle}>No Contact Found</div>
                <div style={styles.emptyText}>
                  Try another name or phone number.
                </div>
              </div>
            )}

            {visibleCount < filteredContacts.length && (
              <div ref={sentinelRef} style={{ height: 20 }} />
            )}
          </div>
        </div>
      </div>

      {addOpen && (
        <div className="popup-overlay" onClick={closeAdd}>
          <div className="popup-card" onClick={(e) => e.stopPropagation()}>
            <div style={styles.popupHeader}>
              <div>
                <div style={styles.popupTitle}>Add New Contact</div>
                <div style={styles.popupSubtitle}>
                  Add a Nexora user to your Contract List.
                </div>
              </div>

              <button
                type="button"
                style={styles.closeButton}
                onClick={closeAdd}
              >
                ×
              </button>
            </div>

            <label style={styles.label}>Phone number</label>

            <input
              className="popup-input"
              type="tel"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder="+8801XXXXXXXXX"
              autoFocus
            />

            {checking && (
              <div style={styles.checking}>Checking Nexora user...</div>
            )}

            {checkMessage && (
              <div style={styles.errorText}>{checkMessage}</div>
            )}

            {foundUser && (
              <div style={{ marginTop: 15 }}>
                <div style={styles.foundCard}>
                  <div style={styles.foundIcon}>✓</div>
                  <div>
                    <div style={styles.foundTitle}>Nexora user found</div>
                    <div style={styles.foundPhone}>
                      {foundUser.phoneNumber}
                    </div>
                  </div>
                </div>

                <label style={styles.label}>Contact name</label>

                <input
                  className="popup-input"
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Enter contact name"
                />

                <button
                  type="button"
                  className="primary-button"
                  style={{ marginTop: 14 }}
                  onClick={saveNewContact}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {editOpen && (
        <div
          className="popup-overlay"
          onClick={() => setEditOpen(false)}
        >
          <div className="popup-card" onClick={(e) => e.stopPropagation()}>
            <div style={styles.popupHeader}>
              <div>
                <div style={styles.popupTitle}>Edit Detail</div>
                <div style={styles.popupSubtitle}>
                  Update this contact's name.
                </div>
              </div>

              <button
                type="button"
                style={styles.closeButton}
                onClick={() => setEditOpen(false)}
              >
                ×
              </button>
            </div>

            <div style={styles.phoneCard}>
              <div style={styles.phoneLabel}>Phone number</div>
              <div style={styles.phoneValue}>{editPhone}</div>
            </div>

            <label style={styles.label}>Name</label>

            <input
              className="popup-input"
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter contact name"
              autoFocus
            />

            <button
              type="button"
              className="primary-button"
              style={{ marginTop: 14 }}
              onClick={saveEdit}
            >
              Save Changes
            </button>
          </div>
        </div>
      )}

      {popup.show && (
        <div className="popup-overlay" onClick={closePopup}>
          <div
            className="popup-card"
            style={{ textAlign: 'center', maxWidth: 350 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={
                popup.type === 'success'
                  ? 'success-icon'
                  : 'error-icon'
              }
            >
              {popup.type === 'success' ? '✓' : '!'}
            </div>

            <div style={styles.successTitle}>{popup.title}</div>
            <div style={styles.successMessage}>{popup.message}</div>

            <button
              type="button"
              className="primary-button"
              onClick={closePopup}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#fafafa',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden'
  },

  card: {
    width: '100%',
    maxWidth: '480px',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f8fafc'
  },

  header: {
    height: '60px',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    borderBottom: '1px solid #eee'
  },

  backButton: {
    width: 38,
    height: 38,
    border: 'none',
    background: 'none',
    color: '#0f172a',
    fontSize: 27,
    cursor: 'pointer',
    padding: 0
  },

  headerTitle: {
    margin: 0,
    color: '#0f172a',
    fontSize: 18,
    fontWeight: 800
  },

  content: {
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: 16,
    paddingBottom: 32
  },

  summaryCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    background: '#fff',
    border: '1px solid #f1f5f9',
    borderRadius: 20,
    padding: 16,
    marginBottom: 12
  },

  summaryLabel: {
    color: '#94a3b8',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase'
  },

  summaryNumber: {
    color: '#0f172a',
    fontSize: 25,
    fontWeight: 800,
    marginTop: 2
  },

  addButton: {
    height: 42,
    padding: '0 15px',
    borderRadius: 13,
    background: '#1e293b',
    color: '#fff',
    fontSize: 12
  },

  searchCard: {
    height: 52,
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    background: '#eef2f7',
    borderRadius: 18,
    padding: '0 14px',
    marginBottom: 14
  },

  searchIcon: {
    color: '#64748b',
    fontSize: 28
  },

  actionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '11px 12px',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    marginBottom: 10
  },

  actionText: {
    color: '#475569',
    fontSize: 12,
    fontWeight: 700
  },

  actionButtons: {
    display: 'flex',
    gap: 6
  },

  selectionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '11px 12px',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    marginBottom: 10
  },

  selectionTitle: {
    color: '#0f172a',
    fontSize: 12,
    fontWeight: 800
  },

  selectionSubtitle: {
    color: '#94a3b8',
    fontSize: 10,
    marginTop: 2
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 9
  },

  contactCard: {
    minHeight: 76,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '10px 12px',
    background: '#fff',
    border: '1px solid #f1f5f9',
    borderRadius: 18,
    cursor: 'pointer'
  },

  selectedCard: {
    border: '2px solid #64748b',
    background: '#f8fafc'
  },

  contactLeft: {
    minWidth: 0,
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 12
  },

  avatarWrap: {
    position: 'relative',
    flexShrink: 0
  },

  avatar: {
    width: 54,
    height: 54,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
    background: '#e2e8f0'
  },

  checkBadge: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: '#1e293b',
    color: '#fff',
    border: '2px solid #fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 900
  },

  contactInfo: {
    minWidth: 0,
    flex: 1
  },

  contactName: {
    color: '#0f172a',
    fontSize: 15,
    fontWeight: 800,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  contactPhone: {
    color: '#64748b',
    fontSize: 12,
    marginTop: 3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  emptyState: {
    textAlign: 'center',
    padding: '65px 20px',
    color: '#94a3b8'
  },

  emptyIcon: {
    fontSize: 38,
    marginBottom: 10
  },

  emptyTitle: {
    color: '#475569',
    fontSize: 15,
    fontWeight: 800
  },

  emptyText: {
    color: '#94a3b8',
    fontSize: 12,
    marginTop: 5,
    lineHeight: 1.5
  },

  popupHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 20
  },

  popupTitle: {
    color: '#0f172a',
    fontSize: 19,
    fontWeight: 800
  },

  popupSubtitle: {
    color: '#94a3b8',
    fontSize: 11,
    lineHeight: 1.45,
    marginTop: 4
  },

  closeButton: {
    width: 32,
    height: 32,
    border: 'none',
    borderRadius: '50%',
    background: '#f1f5f9',
    color: '#64748b',
    fontSize: 22,
    cursor: 'pointer'
  },

  label: {
    display: 'block',
    color: '#334155',
    fontSize: 12,
    fontWeight: 800,
    marginBottom: 8,
    marginTop: 14
  },

  checking: {
    color: '#64748b',
    fontSize: 11,
    marginTop: 8
  },

  errorText: {
    color: '#dc2626',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.45,
    marginTop: 8
  },

  foundCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#f0fdf4',
    border: '1px solid #dcfce7',
    borderRadius: 15,
    padding: 11,
    marginBottom: 14
  },

  foundIcon: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: '#dcfce7',
    color: '#16a34a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 900
  },

  foundTitle: {
    color: '#166534',
    fontSize: 12,
    fontWeight: 800
  },

  foundPhone: {
    color: '#64748b',
    fontSize: 11,
    marginTop: 2
  },

  phoneCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 15,
    padding: 12
  },

  phoneLabel: {
    color: '#94a3b8',
    fontSize: 10,
    fontWeight: 800
  },

  phoneValue: {
    color: '#334155',
    fontSize: 13,
    fontWeight: 700,
    marginTop: 3
  },

  successTitle: {
    color: '#0f172a',
    fontSize: 20,
    fontWeight: 800
  },

  successMessage: {
    color: '#64748b',
    fontSize: 13,
    lineHeight: 1.5,
    margin: '8px 0 20px'
  }
};

export default Contract;
